import { RPCCacheStrategy } from "./types.js";
import { useRPCContext } from "../../contexts/RPCContext.js";
import { useRPCDefaultOpts } from "./defaults.js";
import { useCallback, useDebugValue, useEffect, useRef, useState } from "react";
import { onBrowserVisibilityChange } from "@therms/web-js";
//#region src/hooks/useRPC/index.ts
/**
* This function is a utility for other parts of the software to signal to any hooks that are mounted to refresh.
*
* @note Use this sparingly**
*
* @param reason {string}
*/
function rpcHookRefreshSignal(reason) {
	console.info(`⦿ rpcHookRefreshSignal(${reason})`);
	window.dispatchEvent(new CustomEvent("RPC_HOOK_REFRESH"));
}
function useRPC(request, args, opts = useRPCDefaultOpts) {
	useDebugValue(request);
	const rpcContext = useRPCContext();
	const key = JSON.stringify({
		args,
		request
	});
	opts = {
		...useRPCDefaultOpts,
		...rpcContext.useRPCDefaultOpts || {},
		...opts || {}
	};
	const rpcClient = rpcContext?.rpcClient;
	if (!rpcClient) throw new Error("a RPCClient instance is required in opts or from RPCContext");
	const [response, setResponse] = useState(() => {
		if (opts.cacheStrategy !== RPCCacheStrategy.remoteFirst) return rpcClient.getCallCache(request, args);
	});
	const [firstCallCompleted, setFirstCallCompleted] = useState(false);
	const [isCalling, setIsCalling] = useState(!!opts.fire && !response);
	const stateRef = useRef({
		callKey: "",
		firstCallCompleted,
		inFlightKey: "",
		isCalling,
		isMounted: true,
		opts,
		response
	});
	const getState = () => stateRef.current;
	stateRef.current.firstCallCompleted = firstCallCompleted;
	stateRef.current.isCalling = isCalling;
	stateRef.current.response = response;
	stateRef.current.opts = opts;
	const call = useCallback(async (key, callRequest, callArgs, callOpts = useRPCDefaultOpts, silentCall) => {
		const state = getState();
		state.callKey = key;
		state.isCalling = true;
		if (!silentCall) setIsCalling(true);
		state.lastCall = () => call(key, callRequest, callArgs, callOpts, true);
		const cachedResponse = rpcClient.getCallCache(callRequest, callArgs);
		if (cachedResponse && callOpts.cacheStrategy !== RPCCacheStrategy.remoteFirst) {
			setResponse(cachedResponse);
			setIsCalling(false);
			if (callOpts.cacheStrategy === RPCCacheStrategy.cacheOnly) return cachedResponse;
		}
		state.inFlightKey = state.callKey;
		const thisCallKey = state.inFlightKey;
		try {
			const callResponse = await rpcClient.call(callRequest, callArgs, {
				timeout: callOpts.timeout,
				transport: callOpts.transport
			});
			const state = getState();
			if (state.isMounted && thisCallKey === state.callKey) {
				setResponse(callResponse);
				setIsCalling(false);
			}
			return callResponse;
		} catch (err) {
			const state = getState();
			console.warn(`useRPC#call(${callRequest}) caught: `, err);
			if (state.isMounted && thisCallKey === state.callKey) {
				if (callOpts.cacheStrategy === RPCCacheStrategy.remoteFirst && cachedResponse) setResponse(cachedResponse);
			}
			return Promise.reject(err);
		} finally {
			const state = getState();
			if (thisCallKey === state.callKey) {
				state.inFlightKey = "";
				if (state.isMounted) {
					setIsCalling(false);
					if (!state.firstCallCompleted) setFirstCallCompleted(true);
				}
			}
		}
	}, []);
	useEffect(() => {
		const state = getState();
		if (opts.fire) {
			if (key !== state.callKey) {
				if (!opts.persistPreviousData) setResponse(void 0);
				call(key, request, args, opts).catch(() => null);
			}
		}
	}, [
		args,
		request,
		opts
	]);
	useEffect(() => {
		let shouldCallOnVisible = false;
		let visibilityTimeout;
		stateRef.current.isMounted = true;
		const rpcHookRefreshSignal = () => {
			stateRef.current.lastCall?.().catch(() => null);
		};
		addEventListener("RPC_HOOK_REFRESH", rpcHookRefreshSignal);
		const unsubscribe = onBrowserVisibilityChange((visible) => {
			if (!visible && opts.callOnVisible) visibilityTimeout = setTimeout(() => {
				shouldCallOnVisible = true;
			}, opts.callOnVisibleTimeout || 0);
			else if (opts.callOnVisible && shouldCallOnVisible && opts.fire) {
				stateRef.current.lastCall?.().catch(() => null);
				shouldCallOnVisible = false;
			}
		});
		return () => {
			removeEventListener("RPC_HOOK_REFRESH", rpcHookRefreshSignal);
			stateRef.current.isMounted = false;
			clearTimeout(visibilityTimeout);
			unsubscribe();
		};
	}, []);
	useEffect(() => {
		if (firstCallCompleted) return rpcClient.subscribe({
			args,
			request
		}, (data) => {
			if (stateRef.current.opts?.fire && !stateRef.current.isCalling) setResponse((prev) => {
				if (prev) return {
					...prev,
					...data
				};
				return prev;
			});
		});
	}, [firstCallCompleted]);
	return {
		call: () => call(key, request, args, opts),
		code: response?.code,
		data: response?.data,
		isCalling,
		message: response?.message,
		success: response?.success || false
	};
}
//#endregion
export { RPCCacheStrategy, rpcHookRefreshSignal, useRPC };

//# sourceMappingURL=index.js.map